import { useQueries, useQuery } from '@tanstack/react-query'
import { apiInstance } from 'services/api'
import { useMasquaradeToken } from 'services/masquarade/masquarade.hooks'
import { useAccessToken } from 'services/token/token.hooks'
import type { LeaseContractResponse } from 'services/contracts/hooks/useLeaseContracts'
import { useTermsOfUse } from 'services/terms/terms.hooks'

export interface LeaseContractsRentAccountBalanceResponse {
  id: number
  accountBalanceValue: string
}

// Backend (and CRM) convention: negative value = debt, positive value = overpayment, 0 = settled.
export const parseAccountBalance = (value?: string | null) => {
  const parsed = parseFloat(String(value ?? ''))

  return Number.isFinite(parsed) ? parsed : 0
}

const getRentAccountBalanceQuery = ({
  id,
  accessToken,
  masquaradeToken,
  enabled
}: {
  id?: LeaseContractResponse['id']
  accessToken?: string | null
  masquaradeToken?: unknown
  enabled: boolean
}) => ({
  queryKey: ['leaseContractsRentAccountBalance', id, masquaradeToken],
  queryFn: () =>
    apiInstance<LeaseContractsRentAccountBalanceResponse>({
      url: `/lease/contracts/${id}/rent-account-balance/`,
      method: 'GET',
      headers: {
        authorization: `Bearer ${accessToken}`,
        'mask-authorization': !!masquaradeToken ? `Token ${masquaradeToken}` : undefined
      }
    }).then(({ data }) => data),
  enabled: !!accessToken && !!id && enabled
})

export const useLeaseContractsRentAccountBalance = (id?: LeaseContractResponse['id']) => {
  const { accessToken } = useAccessToken()
  const { masquaradeToken } = useMasquaradeToken()
  const { hasUserConsented } = useTermsOfUse()

  const {
    data: leaseContractsRentAccountBalance = {
      accountBalanceValue: ''
    },
    ...rest
  } = useQuery(
    getRentAccountBalanceQuery({ id, accessToken, masquaradeToken, enabled: hasUserConsented })
  )

  const accountBalance = parseAccountBalance(leaseContractsRentAccountBalance.accountBalanceValue)
  const isOverpayment = accountBalance > 0
  const hasDebt = accountBalance < 0

  return { leaseContractsRentAccountBalance, accountBalance, isOverpayment, hasDebt, ...rest }
}

// Balances of many contracts at once (dashboard). Result is keyed by contract id.
export const useLeaseContractsRentAccountBalances = (ids: LeaseContractResponse['id'][]) => {
  const { accessToken } = useAccessToken()
  const { masquaradeToken } = useMasquaradeToken()
  const { hasUserConsented } = useTermsOfUse()

  const queries = useQueries({
    queries: ids.map((id) =>
      getRentAccountBalanceQuery({ id, accessToken, masquaradeToken, enabled: hasUserConsented })
    )
  })

  const balances = ids.reduce<Record<number, number>>((acc, id, index) => {
    const data = queries[index]?.data

    if (data) {
      acc[id] = parseAccountBalance(data.accountBalanceValue)
    }

    return acc
  }, {})

  const isLoading = queries.some(
    ({ isLoading, fetchStatus }) => isLoading && fetchStatus !== 'idle'
  )

  return { balances, isLoading }
}
